import React, { useState, useEffect, useContext } from "react";
import { TextField, IconButton, Button, Box, Typography } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import Commonmodel from "../../Commonmodel/Commonmodel";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { authHttp } from "../../../services/commonHttpService";
import { apisPath } from "../../../utils/path";
import { CommonContext } from "../../../context/CommonContext";
import { AuthContext } from "../../../context/AuthContext";
import { convertTo24Hour } from "../../../helpers/commonHelper";
import SuccessImg from "../../Images/success_img.svg";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// Validation schema
const slotSchema = Yup.object().shape({
  slots: Yup.array()
    .of(
      Yup.object().shape({
        date: Yup.string(),
        start_time: Yup.string(),
        end_time: Yup.string(),
        total_beds: Yup.string(),
      })
    )
    .test(
      "at-least-one-complete",
      "Please fill at least one complete slot",
      function (slots) {
        return slots.some(
          (slot) => slot.date && slot.start_time && slot.end_time && slot.total_beds
        );
      }
    ),
});

const CreateSlotModal = ({ open, onClose, onSuccess }) => {
  const { setIsLoading, setSnackState } = useContext(CommonContext);
  const { facilityId } = useContext(AuthContext);
  const [existingSlots, setExistingSlots] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch existing slots on mount
  useEffect(() => {
    if (open) {
      fetchExistingSlots();
      setShowSuccess(false);
    }
  }, [open]);

  const fetchExistingSlots = async () => {
    setIsLoading(true);
    try {
      const response = await authHttp.get(apisPath?.getAllSlots);
      if (response?.data?.status) {
        setExistingSlots(response?.data?.data || []);
      }
    } catch (error) {
      setSnackState({
        open: true,
        message: error?.response?.data?.message || "Failed to fetch existing slots",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Check if slot overlaps or duplicates with existing slots
  const checkSlotConflict = (date, startTime, endTime, currentIndex, values) => {
    // Convert to 24hr format for comparison
    const newStart = convertTo24Hour(startTime);
    const newEnd = convertTo24Hour(endTime);

    // Check against existing slots from API
    const hasExistingConflict = existingSlots.some((slot) => {
      if (slot.date !== date) return false;

      const existingStart = slot.start_time.substring(0, 5); // "10:00:00" -> "10:00"
      const existingEnd = slot.end_time.substring(0, 5);

      // Check for exact match or overlap
      return (
        (newStart === existingStart && newEnd === existingEnd) || // Exact match
        (newStart < existingEnd && newEnd > existingStart) // Overlap
      );
    });

    if (hasExistingConflict) return true;

    // Check against other slots in current form
    const hasFormConflict = values.slots.some((slot, index) => {
      if (index === currentIndex || slot.date !== date) return false;
      if (!slot.start_time || !slot.end_time) return false;

      const formStart = convertTo24Hour(slot.start_time);
      const formEnd = convertTo24Hour(slot.end_time);

      return (
        (newStart === formStart && newEnd === formEnd) || // Exact match
        (newStart < formEnd && newEnd > formStart) // Overlap
      );
    });

    return hasFormConflict;
  };

  // Validate end time is greater than start time
  const validateTimeRange = (startTime, endTime) => {
    if (!startTime || !endTime) return true;

    const start = dayjs(startTime);
    const end = dayjs(endTime);

    return end.isAfter(start);
  };

  const handleSubmit = async (values) => {
    try {
      setIsLoading(true);

      // Filter out incomplete slots and prepare payload
      const completeSlots = values.slots.filter(
        (slot) => slot.date && slot.start_time && slot.end_time && slot.total_beds
      );

      if (completeSlots.length === 0) {
        setSnackState({
          open: true,
          message: "Please fill at least one complete slot",
          severity: "error",
        });
        setIsLoading(false);
        return;
      }

      // Validate all time ranges
      for (let i = 0; i < completeSlots.length; i++) {
        const slot = completeSlots[i];
        if (!validateTimeRange(slot.start_time, slot.end_time)) {
          setSnackState({
            open: true,
            message: `Slot ${i + 1}: End time must be greater than start time`,
            severity: "error",
          });
          setIsLoading(false);
          return;
        }
      }

      // Prepare payload
      const payload = completeSlots.map((slot) => ({
        facility_id: parseInt(facilityId),
        date: slot.date,
        start_time: convertTo24Hour(slot.start_time),
        end_time: convertTo24Hour(slot.end_time),
        total_beds: parseInt(slot.total_beds),
        available_beds: parseInt(slot.total_beds), // Same as total_beds initially
      }));

      const response = await authHttp.post(apisPath?.createSlots, payload);

      if (response?.data?.status || response?.data?.success) {
        // Show success screen
        setShowSuccess(true);

        // Optionally call onSuccess to refresh data in background
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess();
        }
      }
    } catch (error) {
      setSnackState({
        open: true,
        message: error?.response?.data?.message || "Failed to create slots",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Formik
      initialValues={{
        slots: [{ date: "", start_time: "", end_time: "", total_beds: "" }],
      }}
      validationSchema={slotSchema}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, setFieldValue, submitForm }) => (
        <Commonmodel
          open={open}
          onClose={onClose}
          title={showSuccess ? "" : "Create Slot"}
          submitLabel="Create Slot"
          showActions={!showSuccess}
          onSubmit={submitForm}
          disableBackdropClick={true}
        >
          {showSuccess ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 4,
              }}
            >
              {/* Success Image */}
              <Box sx={{ mb: 3 }}>
                <img
                  src={SuccessImg}
                  alt="Success"
                  style={{ width: "150px", height: "150px" }}
                />
              </Box>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  textAlign: "center",
                }}
              >
                Slots Created Successfully
              </Typography>

              <Box
                sx={{
                  backgroundColor: "#e8f5e9",
                  borderRadius: "8px",
                  padding: "12px 24px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#2e7d32",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <span style={{ fontSize: "18px" }}>✓</span>
                  Slots have been created and are now available for booking
                </Typography>
              </Box>
            </Box>
          ) : (
            <Form>
              <p className="text-muted mb-3">
                Create time slots for appointments (Maximum 4 slots at a time)
              </p>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <FieldArray name="slots">
                  {({ push, remove }) => (
                    <>
                      {values.slots.map((slot, index) => {
                        return (
                          <div className="row mb-3 align-items-end" key={index}>
                            {/* Date Picker */}
                            <div className="col-md-3 col-6 mb-2">
                              <DatePicker
                                label="Date"
                                format="DD-MM-YYYY"
                                value={slot.date ? dayjs(slot.date) : null}
                                minDate={dayjs()} // Today onwards
                                onChange={(newValue) => {
                                  if (!newValue) {
                                    setFieldValue(`slots.${index}.date`, "");
                                    return;
                                  }
                                  const selectedDate = dayjs(newValue).format("YYYY-MM-DD");
                                  setFieldValue(`slots.${index}.date`, selectedDate);
                                }}
                                slotProps={{
                                  textField: {
                                    variant: "standard",
                                    fullWidth: true,
                                    autoComplete: "off",
                                  },
                                }}
                              />
                            </div>

                            {/* Start Time Picker */}
                            <div className="col-md-3 col-6 mb-2">
                              <TimePicker
                                label="Start Time"
                                value={slot.start_time ? dayjs(slot.start_time) : null}
                                onChange={(newValue) => {
                                  // Always set the value first to allow selection
                                  setFieldValue(`slots.${index}.start_time`, newValue);
                                }}
                                onAccept={(newValue) => {
                                  // Validate AFTER user confirms selection
                                  if (newValue && slot.end_time) {
                                    // Check if start time is less than end time (same slot validation)
                                    if (!validateTimeRange(newValue, slot.end_time)) {
                                      setSnackState({
                                        open: true,
                                        message: "End time must be greater than start time",
                                        severity: "error",
                                      });
                                      setFieldValue(`slots.${index}.start_time`, "");
                                      return;
                                    }

                                    // Check for conflicts with other slots
                                    const hasConflict = checkSlotConflict(
                                      slot.date,
                                      newValue,
                                      slot.end_time,
                                      index,
                                      values
                                    );

                                    if (hasConflict) {
                                      setSnackState({
                                        open: true,
                                        message: "Slot is already available",
                                        severity: "error",
                                      });
                                      setFieldValue(`slots.${index}.start_time`, "");
                                    }
                                  }
                                }}
                                slotProps={{
                                  textField: {
                                    variant: "standard",
                                    fullWidth: true,
                                    autoComplete: "off",
                                  },
                                }}
                              />
                            </div>

                            {/* End Time Picker */}
                            <div className="col-md-3 col-6 mb-2">
                              <TimePicker
                                label="End Time"
                                value={slot.end_time ? dayjs(slot.end_time) : null}
                                onChange={(newValue) => {
                                  // Always set the value first to allow selection
                                  setFieldValue(`slots.${index}.end_time`, newValue);
                                }}
                                onAccept={(newValue) => {
                                  // Validate AFTER user confirms selection
                                  if (newValue && slot.start_time) {
                                    // Check if end time is greater than start time (same slot validation)
                                    if (!validateTimeRange(slot.start_time, newValue)) {
                                      setSnackState({
                                        open: true,
                                        message: "End time must be greater than start time",
                                        severity: "error",
                                      });
                                      setFieldValue(`slots.${index}.end_time`, "");
                                      return;
                                    }

                                    // Check for conflicts with other slots
                                    const hasConflict = checkSlotConflict(
                                      slot.date,
                                      slot.start_time,
                                      newValue,
                                      index,
                                      values
                                    );

                                    if (hasConflict) {
                                      setSnackState({
                                        open: true,
                                        message: "Slot is already available",
                                        severity: "error",
                                      });
                                      setFieldValue(`slots.${index}.end_time`, "");
                                    }
                                  }
                                }}
                                slotProps={{
                                  textField: {
                                    variant: "standard",
                                    fullWidth: true,
                                    autoComplete: "off",
                                  },
                                }}
                              />
                            </div>

                            {/* Total Beds */}
                            <div className="col-md-2 col-6 mb-2">
                              <TextField
                                type="number"
                                label="Total Beds"
                                autoComplete="off"
                                fullWidth
                                variant="standard"
                                value={slot.total_beds}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  // Only allow positive numbers
                                  if (value === "" || parseInt(value) > 0) {
                                    setFieldValue(`slots.${index}.total_beds`, value);
                                  }
                                }}
                                inputProps={{
                                  min: 1,
                                }}
                              />
                            </div>

                            {/* Delete Button */}
                            <div className="col-md-1 col-2 mb-2">
                              {values.slots.length > 1 && (
                                <IconButton color="error" onClick={() => remove(index)}>
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {/* Validation Error */}
                      {errors.slots && touched.slots && (
                        <Typography
                          color="error"
                          variant="caption"
                          sx={{ mb: 2, display: "block" }}
                        >
                          {errors.slots}
                        </Typography>
                      )}

                      {/* Add More Button */}
                      {values.slots.length < 4 && (
                        <Button
                          variant="outlined"
                          startIcon={<AddCircleIcon />}
                          onClick={() =>
                            push({ date: "", start_time: "", end_time: "", total_beds: "" })
                          }
                          sx={{ borderRadius: "20px" }}
                        >
                          Add More
                        </Button>
                      )}

                      {values.slots.length >= 4 && (
                        <Typography
                          variant="caption"
                          sx={{ display: "block", color: "#757575", mt: 1 }}
                        >
                          Maximum 4 slots can be created at a time
                        </Typography>
                      )}
                    </>
                  )}
                </FieldArray>
              </LocalizationProvider>
            </Form>
          )}
        </Commonmodel>
      )}
    </Formik>
  );
};

export default CreateSlotModal;